import {
  formatShowtime,
  pluralSr,
  resolveTitle,
  seatsFreeSr,
  untilShowtimeSr,
  wildcardPhase,
  winnerTitle,
  type BoardScreening,
} from '@movienight/shared';
import { useTitleOrder } from '../lib/TitleOrderProvider';
import { BulbFrame } from './BulbFrame';
import { Face } from './Face';
import { HypeMeter } from './HypeMeter';
import { ROUTES } from '../lib/route';
import type { FlipPhase } from './useBoard';

/**
 * One wide card in the Now Showing stack (DESIGN.md §7).
 *
 * Three columns — poster, the film, the termin rail — collapsing to three
 * stacked blocks on a phone. Principle 3 in the direction it is written:
 * designed at 390px, *allowed* to become a slab on desktop.
 *
 * ## No flip
 *
 * v1.0 turned the card over to reveal the synopsis, runtime, director and
 * cast. The wide card carries the synopsis on its face, so the flip had
 * nothing left to hide: the director joined the facts line, and the cast left
 * the board for the Nerd Glasses (§11), which is the better home for it — it
 * is nerd information and it ought to cost you the glasses.
 *
 * The board gives up its one tactile gesture in the process, which is
 * principle 1. On this surface principle 2 outranks it: the board is
 * *information*, and it is the one place the doc says legibility wins outright.
 * The toy lives everywhere else — including, still, along the bottom of this
 * card.
 *
 * ## The frame
 *
 * Every card wears the marquee (§7a), which means **this component must not
 * clip its own overflow**: the bulbs sit outside the padding box, on top of the
 * border. The inner wrapper is what clips, and the fire overlay lives inside it
 * for the same reason.
 *
 * ## The one flip that survived
 *
 * §7 took the synopsis flip away; §12 keeps this one, and they are not the same
 * gesture. That one was a toy hiding information the wide card already had room
 * for. This one is the only moment on the site where a card *changes what it
 * is* — a slot with a question mark on it becomes a film — and the turn is how
 * everyone watching finds out at the same instant. It is driven from the
 * socket (see `useBoard.reveal`), never from the clock.
 */

export function ScreeningCard({
  screening,
  now,
  meId,
  burning,
  revealing,
  onTapHype,
  onBook,
}: {
  screening: BoardScreening;
  now: Date;
  /** Only so your own face can be marked in the row of people coming. */
  meId: string;
  burning: boolean;
  /** Mid-reveal: 'away' is turned edge-on and holding, 'back' is coming round. */
  revealing: FlipPhase | null;
  onTapHype: () => void;
  onBook: () => void;
}) {
  const { movie, room, seats, hype } = screening;
  const order = useTitleOrder();
  /**
   * §7's two lines, now a rule rather than two fields: the reader's first
   * available language on top, the next distinct one under it. For the common
   * case — Serbian first, an English-language film — that is exactly the
   * "Klub boraca / Fight Club" the card has always drawn.
   */
  const title = resolveTitle(movie, order);
  const when = formatShowtime(screening.startsAt, now);
  const free = seatsFreeSr(seats.free, seats.total);
  const isWildcard = movie === null;
  const phase = wildcardPhase(screening, now);
  const winner = screening.pitchWinner;

  const facts = isWildcard
    ? []
    : [
        movie.year?.toString(),
        movie.tags.slice(0, 2).join(', ') || null,
        movie.runtimeMin ? `${movie.runtimeMin} min` : null,
        movie.director,
      ].filter((part): part is string => Boolean(part));

  return (
    <BulbFrame
      className={[
        'show-card',
        isWildcard ? 'is-wildcard' : '',
        revealing ? `is-flipping is-${revealing}` : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="show-card__inner">
        <div className="show-card__grid">
          <div className="show-card__poster">
            {movie?.posterUrl ? (
              <img src={movie.posterUrl} alt="" loading="lazy" decoding="async" />
            ) : (
              /* Not a loading state, and not an error: a wildcard slot has no
                 film yet, and a film imported with no TMDB key has no art at
                 all. So the frame carries the title instead of a broken-image
                 glyph — a poster with the name on it is a poster, and an em
                 dash in a 2:3 box just looks like something failed to load.
                 aria-hidden because the heading beside it already says this. */
              <div className="show-card__poster-blank" aria-hidden="true">
                {isWildcard ? (
                  <span className="show-card__qmark">?</span>
                ) : (
                  <span className="show-card__poster-title">{title.primary}</span>
                )}
              </div>
            )}
          </div>

          <div className="show-card__body">
            <h3 className="show-card__title">{isWildcard ? 'Glas naroda' : title.primary}</h3>
            {!isWildcard && title.secondary && (
              <p className="show-card__orig">{title.secondary}</p>
            )}

            {isWildcard ? (
              /* Four things this box has to be able to say, and the difference
                 between the last three is the difference between "we are
                 counting", "nobody pitched" and "the room chose an idea rather
                 than a film". See `wildcardPhase`. */
              <>
                {phase === 'voting' && (
                  <>
                    <p className="show-card__tagline">Glasajte za film koji želiš da gledaš!</p>
                    <p className="show-card__synopsis selectable">
                      Cedulja sa najviše glasova dobija ovaj termin.
                      {screening.pitchClosesAt && (
                        <>
                          {' '}
                          Glasanje se zatvara{' '}
                          {untilShowtimeSr(screening.pitchClosesAt, now)}.
                        </>
                      )}
                    </p>
                    <a className="show-card__pitch" href={ROUTES.pitch}>
                      Na tablu
                    </a>
                  </>
                )}

                {phase === 'counting' && (
                  <>
                    <p className="show-card__tagline">Glasanje je zatvoreno.</p>
                    <p className="show-card__synopsis selectable">Brojimo. Samo čas.</p>
                  </>
                )}

                {/* §12's dead end, said out loud. The slot keeps its date and
                    its seats; the film is the host's to set by hand. */}
                {phase === 'empty' && (
                  <>
                    <p className="show-card__tagline">Niko ništa nije predložio.</p>
                    <p className="show-card__synopsis selectable">
                      Termin i dalje stoji — film bira domaćin. Sledeći put zakači nešto.
                    </p>
                    <a className="show-card__pitch" href={ROUTES.pitch}>
                      Na tablu
                    </a>
                  </>
                )}

                {phase === 'chosen' && winner && (
                  <>
                    <p className="show-card__tagline">
                      Narod je izabrao: {winnerTitle(winner, order)}
                    </p>
                    <p className="show-card__synopsis selectable">{winner.text}</p>
                    <p className="show-card__facts">
                      <span className="show-card__fact">predložio {winner.by.nickname}</span>
                      <span className="show-card__fact">film se još traži</span>
                    </p>
                  </>
                )}
              </>
            ) : (
              <>
                {screening.taglineSr && (
                  <p className="show-card__tagline">{screening.taglineSr}</p>
                )}
                {movie.synopsisSr && (
                  <p className="show-card__synopsis selectable">{movie.synopsisSr}</p>
                )}
                {/* The card that came off the corkboard keeps its author. It is
                    the better half of the joke, and it is the first thing lost
                    when the poster arrives. */}
                {winner && (
                  <p className="show-card__won selectable">
                    Ovo je predložio <strong>{winner.by.nickname}</strong> — „{winner.text}”
                  </p>
                )}
              </>
            )}

            {facts.length > 0 && (
              /* Separators are drawn by CSS on every item but the last, rather
                 than rendered before each item. Same dots, but a wrapped line
                 can no longer *start* with one — which is what the phone
                 layout did, leaving "• David Fincher" dangling on its own. */
              <p className="show-card__facts">
                {facts.map((part) => (
                  <span className="show-card__fact" key={part}>
                    {part}
                  </span>
                ))}
              </p>
            )}
          </div>

          {/* The reason anybody opened the page. Nothing in here is stylized
              past legibility — no letter-spacing, no decorative face. */}
          <div className="show-card__termin">
            <h4 className="show-card__termin-label">Termin</h4>
            <p className="show-card__when">
              <span className="show-card__day">{when.headline}</span>
              <span className="show-card__time">{when.time}</span>
            </p>
            <p className="show-card__date">{when.dayMonth}</p>
            <p className="show-card__until">{untilShowtimeSr(screening.startsAt, now)}</p>

            <p className="show-card__room">{room.name}</p>
            {/* Null until a room has a seat map, rather than "0 od 0". */}
            {free && <p className="show-card__seats">{free}</p>}

            {/* Who is actually coming — the more interesting of the card's two
                face rows. Hype is who fancies the film; this is who has put
                themselves in a chair, so it belongs in the termin rail next to
                the time and the room rather than down with the flame. */}
            {seats.who.length > 0 && (
              <div className="show-card__going">
                <p className="show-card__going-label">
                  {seats.who.length}{' '}
                  {pluralSr(seats.who.length, 'dolazi', 'dolaze', 'dolazi')}
                </p>
                <div className="show-card__faces">
                  {seats.who.map((member) => (
                    <Face
                      key={member.id}
                      nickname={member.nickname}
                      avatarUrl={member.avatarUrl}
                      className={`show-card__face${member.id === meId ? ' is-me' : ''}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Booking is the single thing this board exists to get you to, so
                the button lives on the card from the start. It is disabled
                until the room it points at actually has seats — a room with no
                map yet is a room nobody can sit in. Drawing one enables this
                with no layout change, which is what it was designed for. */}
            <button
              type="button"
              className={`show-card__book${seats.mine ? ' is-booked' : ''}`}
              disabled={seats.total === 0}
              title={seats.total === 0 ? 'Soba još nema mapu mesta.' : undefined}
              onClick={onBook}
            >
              {seats.mine ? 'Tvoje mesto' : 'Rezerviši'}
            </button>
          </div>
        </div>

        <HypeMeter hype={hype} onTap={onTapHype} />

        {burning && <div className="show-card__fire" aria-hidden="true" />}
      </div>
    </BulbFrame>
  );
}
